import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Text,
    themeVars,
    ChevronLeftIcon,
    ArrowLeftIcon,
    AlertInline,
    Link
} from '@eds-web/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import queryString from 'query-string';

import { Winning } from '../../types/prize.types';
import { PrizeContentDetail } from './PrizeContent';
import { usePrizeStepCalculation } from '../../hooks/usePrizeStepCalculation';
import { ContentGate } from '../access/ContentGate';
import { CompletedPrize } from './CompletedPrize';
import { device } from '../../constants/breakpoint';
import {
    selectPrizesOffline,
    selectPrizesData,
    selectWinnings,
    selectPrizesDataLoaded,
    selectPrizesLoading
} from '../../selectors/prize.selector';
import { usePrizeActions } from '../../hooks/usePrizeActions';
import { Loading } from '../common/Loading';
import { PRIZE_V2_ROUTER } from '../../routes/routes';
import {
    fetchAccountStatus,
    fetchRetakeAvailable,
    fetchTaxProfile
} from '../../actions/tax.actions';
import {
    isPrizeClaimed,
    isPrizeOffline,
    isPrizeCash,
    isFRMinorPrize
} from '../../utils/prizeUtils';
import { GAME_TITLE } from '../../constants/GameTitle';
import { useContentGate } from '../../hooks/useContentGate';

interface ContainerProps {
    $showAgeGate?: boolean;
}

const Container = styled.div<ContainerProps>`
    padding: 24px;
    max-width: ${(props) => (props.$showAgeGate ? '626px' : '1032px')};
    margin: 0 auto 64px auto;
    transition: max-width 0.3s ease;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 24px;
`;

const BackButton = styled(Link)`
    svg {
        margin: 0 10px 0 0 !important;
    }
    color: rgba(255, 255, 255, 0.95);
    &:hover {
        text-decoration: none;
    }
`;

const MobileBackButton = styled.div`
    display: none;
    @media screen and ${device.laptop} {
        display: block;
    }
`;

const MobileButtonContainer = styled.div`
    display: none;
    @media screen and ${device.laptop} {
        display: flex;
        justify-content: center;
        margin-top: 24px;
        button {
            width: 100%;
        }
    }
`;

const DesktopBackButton = styled.div`
    display: block;
    @media screen and ${device.laptop} {
        display: none;
    }
`;

export const PrizeDetail: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const prizeData = useSelector(selectPrizesData);
    const prizeDataLoaded = useSelector(selectPrizesDataLoaded);
    const loading = useSelector(selectPrizesLoading);
    const { getPrizes } = usePrizeActions();
    const offlinePrize = useSelector(selectPrizesOffline);
    const dispatch = useDispatch();
    const { showEntitlementError } = useContentGate(GAME_TITLE.FORTNITE);

    const identityTaxVerified = Boolean(prizeData?.identityTaxVerified);
    const offlinePayment = Boolean(prizeData?.offlinePayment);
    const queryParams = queryString.parse(location.search);
    const prizeId = queryParams.prizeId as string;

    const [showAgeGate, setShowAgeGate] = useState(false);

    const handleAgeGateVisibilityChange = useCallback((show: boolean) => {
        setShowAgeGate(show);
    }, []);

    useEffect(() => {
        if (!prizeDataLoaded && !showEntitlementError) {
            dispatch(fetchTaxProfile() as any);
            dispatch(fetchRetakeAvailable() as any);
            dispatch(fetchAccountStatus() as any);
            getPrizes();
        }
        window.scrollTo(0, 0);
    }, []);

    // Find the prize from prize redux state
    const winnings = useSelector(selectWinnings);
    const winning = useMemo(
        () => winnings.find((w: Winning) => w.prizeId === prizeId),
        [winnings, prizeId]
    );

    const currentStep = usePrizeStepCalculation(winning, identityTaxVerified, offlinePayment);

    const handleBack = useCallback(() => {
        history.replace({
            pathname: PRIZE_V2_ROUTER
        });
    }, []);

    if (loading) {
        return (
            <div style={{ margin: '64px auto 64px auto' }}>
                <Loading />
            </div>
        );
    }

    if (!winning) {
        return (
            <ContentGate gameId={GAME_TITLE.FORTNITE}>
                <Container $showAgeGate={false}>
                    <Button icon={<ChevronLeftIcon />} variant={'ghost'} onClick={handleBack}>
                        {t('epic.esport.actions.back')}
                    </Button>
                    <div style={{ margin: '64px auto 64px auto' }}>
                        <AlertInline
                            status={'critical'}
                            message={t('epic.esport.prizes.detail.notFound')}
                        />
                    </div>
                </Container>
            </ContentGate>
        );
    }

    const isClaimed = isPrizeClaimed(winning);
    const isOffline = isPrizeOffline(winning, offlinePrize);
    const isCash = isPrizeCash(winning);
    const isFRMinor = isFRMinorPrize(winning, offlinePrize);

    return (
        <ContentGate gameId={winning?.gameId}>
            <Container $showAgeGate={showAgeGate}>
                {!showAgeGate && (
                    <Header>
                        <BackButton variant="secondary" onClick={handleBack}>
                            <DesktopBackButton>
                                <ChevronLeftIcon />
                                {t('epic.esport.actions.back')}
                            </DesktopBackButton>
                            <MobileBackButton>
                                <ArrowLeftIcon />
                            </MobileBackButton>
                        </BackButton>
                        <Text
                            variant="headingMD"
                            style={{
                                color: themeVars.color.transparent.light['100'],
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden'
                            }}
                        >
                            {t('epic.esport.prizes.detail.title', {
                                eventName: winning?.eventName
                            })}
                        </Text>
                    </Header>
                )}

                {isClaimed ? (
                    <div style={{ padding: '16px 0' }}>
                        <CompletedPrize
                            isCash={isCash}
                            isFRMinor={isFRMinor}
                            isOffline={isOffline}
                        />
                        <MobileButtonContainer>
                            <Button onClick={handleBack} size="lg" variant="solid">
                                {t('epic.esport.actions.back')}
                            </Button>
                        </MobileButtonContainer>
                    </div>
                ) : (
                    <PrizeContentDetail
                        currentStep={currentStep}
                        identityTaxVerified={identityTaxVerified}
                        winning={winning}
                        isOffline={isOffline}
                        isCash={isCash}
                        onAgeGateVisibilityChange={handleAgeGateVisibilityChange}
                    />
                )}
            </Container>
        </ContentGate>
    );
};
